import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { FinanceApiService, FxRateSnapshotRecord } from './finance-api.service';

type FxRateDetailTab = 'general' | 'history';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-fx-rate-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, InputNumberModule, CurrencySelectorComponent],
  template: `
    <ain-page [title]="isCreateMode() ? t('finance.multiCurrency.fxRates.createTitle') : t('finance.multiCurrency.fxRates.detailTitle')" [description]="t('finance.multiCurrency.fxRates.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('finance.multiCurrency.fxRates.newRecord') : (form.fromCurrencyCode + ' → ' + form.toCurrencyCode) }}</h3>
            <p>{{ t('finance.multiCurrency.fxRates.effectiveDate') }}: {{ form.effectiveDateUtc || '-' }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/financial/control-center/fx-rates']" [queryParams]="listQueryParams()" class="back-link">{{ t('catalog.action.backToList') }}</a>
            @if (!isCreateMode() && selectedRate()?.approvalStatus !== 'Approved') {
              <button pButton type="button" severity="secondary" [label]="t('finance.multiCurrency.actions.approve')" (click)="approve()"></button>
              <button pButton type="button" severity="secondary" [label]="t('finance.multiCurrency.actions.reject')" (click)="reject()"></button>
            }
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </div>
        </section>

        <section class="tabs">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('catalog.tabs.general') }}</button>
          <button type="button" [class.active]="activeTab() === 'history'" [disabled]="isCreateMode()" (click)="activeTab.set('history')">{{ t('finance.multiCurrency.tabs.history') }}</button>
        </section>

        @if (activeTab() === 'general') {
          <section class="form-grid">
            <label>
              <span>{{ t('finance.multiCurrency.fxRates.effectiveDate') }}</span>
              <input pInputText type="date" [(ngModel)]="form.effectiveDateUtc" />
            </label>
            <label>
              <span>{{ t('finance.multiCurrency.fxRates.fromCurrency') }}</span>
              <ain-currency-selector [(ngModel)]="form.fromCurrencyCode" [showSearch]="true"></ain-currency-selector>
            </label>
            <label>
              <span>{{ t('finance.multiCurrency.fxRates.toCurrency') }}</span>
              <ain-currency-selector [(ngModel)]="form.toCurrencyCode" [showSearch]="true"></ain-currency-selector>
            </label>
            <label>
              <span>{{ t('finance.multiCurrency.fxRates.rate') }}</span>
              <p-inputNumber [(ngModel)]="form.rate" [useGrouping]="false" [minFractionDigits]="2" [maxFractionDigits]="8" />
            </label>
          </section>
        }

        @if (activeTab() === 'history') {
          <section class="history">
            <article class="history-item">
              <h4>{{ t('columns.status') }}</h4>
              <p>{{ selectedRate()?.approvalStatus }}</p>
            </article>
            <article class="history-item">
              <h4>{{ t('finance.multiCurrency.fxRates.approvedBy') }}</h4>
              <p>{{ selectedRate()?.approvedByUserId || '-' }}</p>
            </article>
            <article class="history-item">
              <h4>{{ t('finance.multiCurrency.fxRates.approvedAt') }}</h4>
              <p>{{ selectedRate()?.approvedAtUtc || '-' }}</p>
            </article>
          </section>
        }

        @if (error()) {
          <p class="error">{{ error() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
    .header-card h3 { margin: 0; }
    .header-card p { margin: 6px 0 0; color: #64748b; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .tabs { display: flex; gap: 8px; flex-wrap: wrap; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; margin-top: 10px; }
    .tabs button { border: 1px solid #cbd5e1; border-radius: 999px; background: white; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { border-color: #1d4ed8; color: #1d4ed8; font-weight: 700; }
    .tabs button:disabled { opacity: .45; cursor: not-allowed; }
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; margin-top: 10px; }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid span { color: #475569; font-size: 12px; font-weight: 600; }
    .history { display: grid; gap: 8px; margin-top: 10px; }
    .history-item { border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; }
    .history-item h4 { margin: 0 0 6px; font-size: 14px; }
    .history-item p { margin: 0; color: #475569; font-size: 12px; }
    .error { margin: 10px 0 0; color: #b91c1c; }
  `]
})
export class FinanceControlCenterFxRateDetailComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly error = signal('');
  readonly activeTab = signal<FxRateDetailTab>('general');
  readonly isCreateMode = signal(false);
  readonly selectedRate = signal<FxRateSnapshotRecord | null>(null);

  readonly form = {
    effectiveDateUtc: '',
    fromCurrencyCode: '',
    toCurrencyCode: '',
    rate: 1
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set(this.t('finance.multiCurrency.errors.notFound'));
      this.loading.set(false);
      return;
    }

    if (id === 'new') {
      this.isCreateMode.set(true);
      this.form.fromCurrencyCode = this.route.snapshot.queryParamMap.get('fromCurrencyCode') ?? '';
      this.form.toCurrencyCode = this.route.snapshot.queryParamMap.get('toCurrencyCode') ?? '';
      this.form.effectiveDateUtc = new Date().toISOString().slice(0, 10);
      this.loading.set(false);
      return;
    }

    this.loadRateById(id);
  }

  save(): void {
    this.error.set('');
    if (!this.form.effectiveDateUtc || !this.form.fromCurrencyCode.trim() || !this.form.toCurrencyCode.trim() || this.form.rate <= 0) {
      this.error.set(this.t('finance.multiCurrency.errors.requiredFields'));
      return;
    }

    if (this.isCreateMode()) {
      this.api.createFxRate({
        effectiveDateUtc: this.form.effectiveDateUtc,
        fromCurrencyCode: this.form.fromCurrencyCode.trim(),
        toCurrencyCode: this.form.toCurrencyCode.trim(),
        rate: Number(this.form.rate)
      }).subscribe({
        next: created => {
          void this.router.navigate(['/financial/control-center/fx-rates', created.id], { queryParams: this.listQueryParams() });
        },
        error: () => this.error.set(this.t('common.saveFailed'))
      });
      return;
    }

    const current = this.selectedRate();
    if (!current) {
      return;
    }

    this.api.updateFxRate(current.id, { rate: Number(this.form.rate) }).subscribe({
      next: updated => {
        this.selectedRate.set(updated);
        this.syncForm(updated);
      },
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  approve(): void {
    const current = this.selectedRate();
    if (!current) {
      return;
    }

    this.api.approveFxRate(current.id).subscribe({
      next: updated => this.selectedRate.set(updated),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  reject(): void {
    const current = this.selectedRate();
    if (!current) {
      return;
    }

    this.api.rejectFxRate(current.id).subscribe({
      next: updated => this.selectedRate.set(updated),
      error: () => this.error.set(this.t('common.saveFailed'))
    });
  }

  private loadRateById(id: string): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listFxRates().subscribe({
      next: rows => {
        const rate = rows.find(item => item.id === id) ?? null;
        if (!rate) {
          this.error.set(this.t('finance.multiCurrency.errors.notFound'));
          this.loading.set(false);
          return;
        }

        this.selectedRate.set(rate);
        this.syncForm(rate);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.loadFailed'));
        this.loading.set(false);
      }
    });
  }

  private syncForm(rate: FxRateSnapshotRecord): void {
    this.form.effectiveDateUtc = (rate.effectiveDateUtc ?? '').slice(0, 10);
    this.form.fromCurrencyCode = rate.fromCurrencyCode;
    this.form.toCurrencyCode = rate.toCurrencyCode;
    this.form.rate = rate.rate;
  }

  listQueryParams(): Record<string, string> {
    return {
      effectiveDateUtc: this.route.snapshot.queryParamMap.get('effectiveDateUtc') ?? '',
      fromCurrencyCode: this.route.snapshot.queryParamMap.get('fromCurrencyCode') ?? '',
      toCurrencyCode: this.route.snapshot.queryParamMap.get('toCurrencyCode') ?? '',
      status: this.route.snapshot.queryParamMap.get('status') ?? '',
      showFilters: this.route.snapshot.queryParamMap.get('showFilters') ?? '1'
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
